import type { Freshness, PersonResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { AttentionItem } from '@/components/pulse/attention-item';
import { BuiltThisWeek } from '@/components/pulse/built-this-week';
import { DayControl, NotRecorded, useBusinessDate } from '@/components/pulse/day';
import { IncompleteMark } from '@/components/pulse/freshness';
import { BackIcon } from '@/components/pulse/icons';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { NextList, WorkloadBar, WorkloadChips } from '@/components/pulse/workload';
import { usePerson, useSaveNote } from '@/domains/people/api';
import { useShell } from '@/domains/shell/api';

const STATE_CHIP: Readonly<Record<string, string>> = {
  Merged: 'c-merged',
  Approved: 'c-approved',
  'In review': 'c-review',
  Open: 'c-review',
  'Changes requested': 'c-changes',
  'No reviewer': 'c-warn',
  Commented: 'c-kind',
  Completed: 'c-merged',
};

/** One member's day (PER-1 to PER-5), built from the prototype's `viewPerson()`. */
export function PersonPage(): React.ReactElement {
  const { userId = '' } = useParams();
  const { date, today } = useBusinessDate();
  const page = usePerson(userId, date, today);
  const shell = useShell();
  const data = page.data;
  return (
    <>
      <Link className="linkish" to={date === today ? '/' : `/?date=${date}`} style={{ alignSelf: 'flex-start' }}>
        <BackIcon /> Team day
      </Link>
      <PageHeader
        eyebrow={data === undefined ? '' : `${data.dateLabel} · ${data.member.roleLine}`}
        title={data?.member.displayName ?? ''}
        sub={data === undefined ? '' : `${data.tiles.builtToday} things built, ${data.tiles.merged} merged. Every row links to its PR.`}
        right={<DayControl date={date} today={today} />}
      />
      {page.isError ? <span className="sub">There is no one by that name on the roster.</span> : null}
      {data !== undefined ? <PersonView data={data} freshness={shell.data?.freshness ?? null} /> : null}
    </>
  );
}

/** The signed-in member's note for today (NOTE-1 to NOTE-3), from the prototype's note card. */
export function NoteEditor({ note }: { readonly note: string | null }): React.ReactElement {
  const [text, setText] = React.useState(note ?? '');
  const save = useSaveNote();
  const status = save.isError ? (save.error instanceof Error ? save.error.message : 'The note could not be saved.') : (save.data?.message ?? '');
  return (
    <section className="card pad stack" style={{ gap: 12 }}>
      <h2>Blockers and tomorrow</h2>
      <label htmlFor="note-today" style={{ fontSize: 13, color: 'var(--muted)' }}>
        Only you know this part. It shows on the team day.
      </label>
      <textarea id="note-today" rows={6} maxLength={NOTE_MAX_CHARACTERS} placeholder="What's blocking you, and what's next tomorrow" value={text} onChange={(event) => setText(event.target.value)} />
      <div className="row" style={{ gap: 12 }}>
        <button type="button" className="btn primary" disabled={save.isPending} onClick={() => save.mutate(text)}>
          Save note
        </button>
        <span role="status" style={{ fontSize: 13, color: save.isError ? 'var(--bad-ink)' : 'var(--accent-ink)', fontWeight: 500 }}>
          {status}
        </span>
      </div>
    </section>
  );
}

/** NOTE-1: a note is up to 2,000 characters. */
const NOTE_MAX_CHARACTERS = 2000;

export function PersonView({ data, freshness }: { readonly data: PersonResponse; readonly freshness: Freshness | null }): React.ReactElement {
  const { tiles, thisWeek } = data;
  const firstName = data.member.displayName.split(' ')[0] ?? data.member.displayName;
  return (
    <>
      <Kpis
        items={[
          { label: 'Built today', value: tiles.builtToday, sub: 'PRs with their commits today' },
          { label: 'Merged', value: tiles.merged, sub: 'Across ten24group' },
          { label: 'Reviewed', value: tiles.reviewed, sub: 'PRs they reviewed' },
          { label: 'Open PRs', value: tiles.openPullRequests ?? '–', sub: 'Still open tonight' },
        ]}
      />
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 360px' }}>
        <div className="stack" style={{ gap: 16 }}>
          <BuiltThisWeek thisWeek={thisWeek} freshness={freshness} />
          {data.sections.map((section) => (
            <section key={section.productName} className="card pad stack" style={{ gap: 14 }}>
              <h2>{section.productName}</h2>
              {section.groups.map((group) => (
                <div key={group.title} className="stack">
                  <span className="eyebrow" style={{ paddingBottom: 6 }}>
                    {group.title}
                  </span>
                  {group.note !== null ? <span style={{ fontSize: 13, color: 'var(--muted)', paddingBottom: 6 }}>{group.note}</span> : null}
                  {group.collapsed !== null ? (
                    <div className="prow" style={{ gridTemplateColumns: 'minmax(0,1fr)' }}>
                      <span>
                        {group.collapsed}:{' '}
                        {group.rows.map((row, index) => (
                          <React.Fragment key={row.url}>
                            {index > 0 ? ', ' : ''}
                            <SourceLink href={row.url}>{row.ref}</SourceLink>
                          </React.Fragment>
                        ))}
                      </span>
                    </div>
                  ) : (
                    group.rows.map((row) => (
                      <div key={`${row.url}${row.ref}`} className="prow">
                        <SourceLink className="mono" href={row.url} style={{ fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>
                          {row.ref}
                        </SourceLink>
                        <div className="stack" style={{ gap: 4 }}>
                          <span>
                            {row.storyId !== null && row.storyId !== row.ref ? <b style={{ fontWeight: 600 }}>{row.storyId} </b> : null}
                            {row.title}
                          </span>
                          {row.note !== null ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>{row.note}</span> : null}
                        </div>
                        <div className="prow-chips">
                          {row.kind !== null ? <span className="chip c-kind">{row.kind}</span> : null}
                          {row.state !== null ? <span className={`chip ${STATE_CHIP[row.state] ?? 'c-kind'}`}>{row.state}</span> : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </section>
          ))}
          {data.sections.length === 0 ? (
            <section className="card pad">
              <span style={{ color: 'var(--ink-2)' }}>Nothing from {firstName} this day.</span>
            </section>
          ) : null}
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2>Next up</h2>
            {!data.recorded ? <NotRecorded /> : <NextList items={data.nextUp} />}
            {data.nextUpNote !== null ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>{data.nextUpNote}</span> : null}
          </section>
          <section className="card pad stack" style={{ gap: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2>
                Workload <IncompleteMark figure="workload" freshness={freshness} />
              </h2>
              {data.recorded ? (
                <span className="num" style={{ fontSize: 22, fontWeight: 600 }}>
                  {data.workload.total}
                </span>
              ) : null}
            </div>
            {!data.recorded ? (
              <NotRecorded />
            ) : (
              <>
                <WorkloadBar summary={data.workload} />
                <WorkloadChips summary={data.workload} />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>{data.workload.sources}</span>
              </>
            )}
          </section>
          {data.noteEditable ? (
            <NoteEditor note={data.note} />
          ) : (
            <section className="card pad stack" style={{ gap: 12 }}>
              <h2>Blockers and tomorrow</h2>
              <span style={{ color: data.note === null ? 'var(--muted)' : 'var(--ink)' }}>{data.note ?? 'Not added yet'}</span>
            </section>
          )}
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2>Waiting on others</h2>
            {!data.recorded ? <NotRecorded /> : null}
            {data.recorded && data.waitingOnOthers.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing of {firstName}&apos;s waits on anyone.</span> : null}
            {data.waitingOnOthers.map((group) => (
              <div key={group.who} className="stack" style={{ gap: 6 }}>
                <span style={{ fontWeight: 600 }}>{group.who}</span>
                <span style={{ color: 'var(--ink-2)' }}>
                  {group.items.map((row, index) => (
                    <React.Fragment key={row.url}>
                      {index > 0 ? ', ' : ''}
                      <SourceLink href={row.url}>{row.ref}</SourceLink>
                    </React.Fragment>
                  ))}
                </span>
              </div>
            ))}
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Stuck work</h2>
            {!data.recorded ? <NotRecorded /> : null}
            {data.recorded && data.stuck.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing active went quiet this week.</span> : null}
            {data.stuck.map((row) => (
              <AttentionItem key={row.ref} row={row} />
            ))}
          </section>
        </aside>
      </div>
    </>
  );
}
